import { BomActionTypes, ActionTypes } from 'actionsConstants';
import { SaveLoadProjectActionTypes } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/saveLoadProjectActionsConstants';
import { createReducer } from '__common/utils/helpers';
import { isNxtHorizon, isSFMFamily, isSMFamily, isSMTiltOrFlush, isSMTiltPR, products } from '__common/constants/products';
import { restorePartQty } from './components/utils/loadBomSelectionsQtys';
import { parseAttachmentsList } from './bomHelpers';
import {
  addSelectedFlashingToPartsList,
  addSelectedStandoffsToPartsList,
  addSFMAttachmentsToPartsList,
  enableAllParts,
  getSFMAttachmentsPartsNumbers,
  selectAttachment,
} from 'bom/bomHelpers';
import { addClamps, addNxtClamps } from './utils/clamps';
import { BND_T_BOLT_NUT_3_8_3_4_SS, PART_RM10_EVO_MODULE_CLAMP_BULK, PART_RM10_EVO_MODULE_CLAMP_KIT, PART_RM10_EVO_MODULE_CLAMP_SIDE_BOLT_BULK, mlpePartNumber } from '__common/constants/parts';
import { nxtMlpeMountDomesticPart } from '__common/utils/versionCompare/versionCompare';
import { LIVE_BOM_AUTO_REFETCH_DEBOUNCE_MS } from './liveBomConstants';

export const bomState: bomState = {
  total_watts: 0,
  total_modules: 0,
  productId: 0,
  projectId: '',
  partsList: [],
  accessoriesList: [],
  attachmentsList: [],
  tileReplacementList: {},
  selectedAttachmentType: { selected: false, parts: [] },
  extraParameters: {},
  isLoading: false,
  bomSelectionsLoading: false,
  stdClampsFetching: false,
  stdClampsFetched: false,
  isReady: false,
  project_name: '',
  error: null,
  bomPk: null,
  reload_selections: false,
  goToEngineeringFromBom: false,
  bomChanged: false,
  created: false,
  isClampsChanged: false,
  structureTypeCount: 0,
  auroraReport: {
    show: false,
    diableRetryButton: null,
    auroraReportsDataUrl: null,
    loading: null,
    error: null,
    errorMessages: null,
  },
  osReport: {
    show: false,
    diableRetryButton: null,
    osReportsDataUrl: null,
    loading: null,
    error: null,
    errorMessages: null,
  },
  liveBomStatus: 'empty',
  liveBomLastUpdatedAt: null,
  liveBomNextRecalculateAt: null,
  liveBomPreviousSnapshot: null,
  liveBomCurrentSnapshot: null,
  liveBomDeltaByPartKey: {},
  liveBomFilters: {
    changedOnly: false,
    warningsOnly: false,
    groupBy: 'none',
    searchText: '',
  },
  liveBomSidebarCollapsed: false,
  liveBomError: null,
};

function normalizeBomPartsList(partsList: bomPartsList, useSuggestedQuantity = false): bomPartsList {
  const order: string[] = [];
  const byPartNumber: Record<string, bomPart> = {};

  (partsList || []).forEach((part: bomPart, index: number) => {
    const partNumber = part.number ? part.number.trim() : '';
    const key = partNumber || `${part.type || ''}|${part.description || ''}|${index}`;
    const normalizedPart = JSON.parse(JSON.stringify(part));

    if (partNumber) {
      normalizedPart.number = partNumber;
    }
    if (useSuggestedQuantity) {
      normalizedPart.quantity = normalizedPart.suggested_quantity;
    }
    if (!byPartNumber[key]) {
      order.push(key);
    }

    byPartNumber[key] = normalizedPart;
  });

  return order.map(partNumber => byPartNumber[partNumber]);
}

function getLiveBomPartsPatch(state: bomState, partsList: bomPartsList) {
  return state.liveBomCurrentSnapshot
    ? { liveBomCurrentSnapshot: partsList }
    : {};
}

export default {
  bom: createReducer(bomState, {
    [BomActionTypes.ADD_PART_TO_BOM](state, action) {
      const { part } = action.payload;
      restorePartQty(state, part);
      const partsList = normalizeBomPartsList([...state.partsList, part]);
      return { ...state, partsList, ...getLiveBomPartsPatch(state, partsList) };
    },
    [BomActionTypes.ADD_RM10_MODULE_AND_HEX_BOLT](state, action) {
      const { qty } = action.payload;
      let { parts } = action.payload;
      parts = parts.map((part) => {
        part.quantity = qty;
        part.suggested_quantity = qty;
        restorePartQty(state, part);
        return part;
      });
      const partsList = state.partsList
        .filter(part => part.number !== '310750')
        .filter(part => part.number !== '310751')
        .concat(parts);
      return { ...state, partsList };
    },
    [BomActionTypes.ADD_RM10_EVO_MODULE_CLAMP_KIT](state, action) {
      const { qty } = action.payload;
      let { parts } = action.payload;
      parts = parts.map((part) => {
        part.quantity = qty;
        part.suggested_quantity = qty;
        restorePartQty(state, part);
        return part;
      });
      const partsList = state.partsList
        .filter(part => part.number !== PART_RM10_EVO_MODULE_CLAMP_SIDE_BOLT_BULK)
        .filter(part => part.number !== PART_RM10_EVO_MODULE_CLAMP_BULK)
        .concat(parts);
      return { ...state, partsList };
    },
    [BomActionTypes.ADD_ATTACHMENT_TO_BOM](state, action) {
      const { attachment } = action.payload;

      const partsList = state.partsList.filter((part) => {
           if (attachment.type === "Flashing") {
          return part.number!==attachment.number;
        }
        return part.type !== attachment.type || part.number.includes('304001') || part.number.includes('304000') || part.number.includes('A1LF02M');
      });
      if (attachment.number.length) {
        const { extraParameters: { total_feet, productId } } = state;
        if (isSMTiltOrFlush(productId)  && total_feet) {
          attachment['quantity'] = total_feet;
          attachment['suggested_quantity'] = total_feet;
        }
        restorePartQty(state, attachment);
        partsList.push(attachment);
      }
      return { ...state, partsList };
    },
    [BomActionTypes.ADD_CLAMPS](state: bomState, action) {
      const { midclamps, endclamps } = action.payload;
      const partsList = normalizeBomPartsList(addClamps(state.partsList, midclamps, endclamps, state.productId));
      const liveBomPatch = state.liveBomCurrentSnapshot
        ? { liveBomCurrentSnapshot: partsList, liveBomStatus: state.liveBomStatus === 'recalculating' ? 'synced' : state.liveBomStatus }
        : {};

      if (state.stdClampsFetching) {
        return { ...state, partsList, ...liveBomPatch, stdClampsFetching: false, stdClampsFetched: true };
      }

      return { ...state, partsList, ...liveBomPatch };
    },
    [BomActionTypes.ADD_NXT_CLAMPS](state: bomState, action) {
      const { clamps } = action.payload;
      const partsList = normalizeBomPartsList(addNxtClamps(state.partsList,clamps));
      const liveBomPatch = state.liveBomCurrentSnapshot
        ? { liveBomCurrentSnapshot: partsList, liveBomStatus: state.liveBomStatus === 'recalculating' ? 'synced' : state.liveBomStatus }
        : {};

      return { ...state, partsList, ...liveBomPatch };
    },
    [BomActionTypes.ADD_FRONT_TRIM_END_CAP](state:bomState, action) {

      const partsList = state.partsList;

      const isFrontTrim = partsList.find(part => part.number.includes('206072'));
      const isTrimClip = partsList.find(part => part.number.includes('008025'));


      if (!isFrontTrim || isTrimClip) {
        return { ...state };
      }

      const { clips, midclamps, endclamps } = action.payload;

      if (!clips) {
        return { ...state };
      }

      if(clips.quantity !== 0){

        clips.quantity = clips.quantity;
        restorePartQty(state, clips);
        clips.suggested_quantity = clips.quantity;

        partsList.push(clips);

      }


      partsList.map((part) => {
        if(part.type === midclamps.type){
          part.quantity += parseInt(midclamps.quantity);
          part.suggested_quantity += parseInt(midclamps.quantity);
        }
        if(part.type === endclamps.type){
          part.quantity += parseInt(endclamps.quantity);
          part.suggested_quantity += parseInt(endclamps.quantity);
        }
        return part;
      });

      return { ...state, partsList };


    },
    [BomActionTypes.REMOVE_EXTRA_MID_AND_END_CLAMP_QUANTITY](state:bomState, action) {
      const { midclamps, endclamps } = action.payload;
      const partsList = state.partsList;
      partsList.map(part => {
        if(part.type === midclamps.type){
          part.quantity -= parseInt(midclamps.quantity);
          part.suggested_quantity -= parseInt(midclamps.quantity);
        }
        if(part.type === endclamps.type){
          part.quantity -= parseInt(endclamps.quantity);
          part.suggested_quantity -= parseInt(endclamps.quantity);
        }
        return part;
      });

      return { ...state, partsList };
    },
    [BomActionTypes.ADD_RAILS_TO_BOM](state, action) {
      let partsListWithoutRails = state.partsList
        .filter((part: bomPart) => part.type !== 'Rail')
        .filter((part: bomPart) => part.type !== 'Splice')
        .filter((part: bomPart) => part.type !== 'Cap Strip')
        .filter((part: bomPart) => part.type !== 'Cap Strip Screw');

      const attachmentKeywords = ['attachment', 'flashing'];
      const isPartAnAttachment = (part: bomPart) => attachmentKeywords.some(keyword => part.type.toLowerCase().includes(keyword));
      const isAttachmentPresent = action.payload.rails.rails.some((rail: bomPart) => isPartAnAttachment(rail));
      if (isAttachmentPresent) partsListWithoutRails = partsListWithoutRails.filter((part: bomPart) => !isPartAnAttachment(part));
      if (isSMTiltOrFlush(state.extraParameters.productId) || isNxtHorizon(state.extraParameters.productId) || isSMTiltPR(state.extraParameters.productId)) {
        partsListWithoutRails = partsListWithoutRails.map((part: bomPart)=>{
          if (isPartAnAttachment(part) && action.payload.rails.total_feet && !isSMTiltPR(state.extraParameters.productId) && !isNxtHorizon(state.extraParameters.productId)) {
            part['quantity'] = action.payload.rails.total_feet;
            part['suggested_quantity'] = action.payload.rails.total_feet;
          }
          if (action.payload.rails?.total_splices && part.type.toLowerCase().includes("splice")) {
            part['quantity'] = action.payload.rails.total_splices;
            part['suggested_quantity'] = action.payload.rails.total_splices;
          }
          return part;
        });
      }
      const rails = normalizeBomPartsList(action.payload.rails.rails, true);
      const partsWithNewRails = partsListWithoutRails.concat(rails);
      let newState = {
        ...state,
        partsList: partsWithNewRails,
        ...(state.liveBomCurrentSnapshot
          ? {
              liveBomCurrentSnapshot: partsWithNewRails,
              liveBomStatus: state.liveBomStatus === 'recalculating' ? 'synced' : state.liveBomStatus,
            }
          : {}),
      };
      const total_feet = action.payload.rails.total_feet;
      const total_feet_5_deg_tilt =  action.payload.rails?.total_feet_5_deg_tilt ;
      const total_feet_10_deg_tilt = action.payload.rails?.total_feet_10_deg_tilt ;
      if (total_feet) {
        const extraParameters = { ...state.extraParameters, total_feet };
        newState = { ...newState, extraParameters };
      }
      if (total_feet_5_deg_tilt > 0) {
        const extraParameters = { ...state.extraParameters, total_feet_5_deg_tilt };
        newState = { ...newState, extraParameters };
      }
      if (total_feet_10_deg_tilt > 0) {
        const extraParameters = { ...state.extraParameters, total_feet_10_deg_tilt };
        newState = { ...newState, extraParameters };
      }
      return newState;
    },
    [BomActionTypes.ADD_TILE_HOOK](state: bomState) {
      const attachment = state.attachmentsList['Tile Hook'].parts[0];
      if (attachment.number.length) {
        const { extraParameters: { total_feet, productId } } = state;
        if (isSMTiltOrFlush(productId)  && total_feet) {
          attachment['quantity'] = total_feet;
          attachment['suggested_quantity'] = total_feet;
        }
        restorePartQty(state, attachment);
        state.partsList.push(attachment);
      }
      return { ...state };
    },
    [BomActionTypes.ADD_FLASHKIT_PRO](state: bomState) {
      const attachment = state.attachmentsList['Flashkit Pro'].parts[0];
      if (attachment.number.length) {
        const { extraParameters: { total_feet, productId } } = state;
        if (isSMTiltOrFlush(productId)  && total_feet) {
          attachment['quantity'] = total_feet;
          attachment['suggested_quantity'] = total_feet;
        }
        restorePartQty(state, attachment);
        state.partsList.push(attachment);
      }
      return { ...state };
    },
    [BomActionTypes.ADD_FLASHKIT_PRO_SB](state: bomState) {
      restorePartQty(state, state.attachmentsList['Flashkit Pro SB'].parts[0]);
      state.partsList.push(state.attachmentsList['Flashkit Pro SB'].parts[0]);
      return { ...state };
    },
    [BomActionTypes.ADD_FLASHLOC_COMP_KIT](state: bomState) {
      const attachment = state.attachmentsList['FLASHLOC COMP KIT'].parts[0];
      if (attachment.number.length) {
        const { extraParameters: { total_feet, productId } } = state;
        if (isSMTiltOrFlush(productId)  && total_feet) {
          attachment['quantity'] = total_feet;
          attachment['suggested_quantity'] = total_feet;
        }
        restorePartQty(state, attachment);
        state.partsList.push(attachment);
      }
      return { ...state };
    },
    [BomActionTypes.ADD_STRONGHOLD_ATT_COMP_NON_SEALANT](state: bomState) {
      state.partsList.filter(part => !part.number.includes('SHCPKT'));
      restorePartQty(state, state.attachmentsList['STRONGHOLD ATT COMP - NON SEALANT'].parts[0]);
      state.partsList.push(state.attachmentsList['STRONGHOLD ATT COMP - NON SEALANT'].parts[0]);
      return { ...state };
    },
    [BomActionTypes.ADD_STRONGHOLD_ATT_KIT_COMP](state: bomState) {
      state.partsList.filter(part => !part.number.includes('SHCPKT'));
      restorePartQty(state, state.attachmentsList['STRONGHOLD ATT KIT COMP'].parts[0]);
      state.partsList.push(state.attachmentsList['STRONGHOLD ATT KIT COMP'].parts[0]);
      return { ...state };
    },
    [BomActionTypes.ADD_TILE_REPLACEMENT_ITEM](state: bomState, action) {
      const { group, selectedGroupItem } = action.payload;
      const partToAdd = state.tileReplacementList[group][selectedGroupItem];
      let partNoPattern = partToAdd.number;
      if (group === 'tiles') {
        partNoPattern = partNoPattern.slice(0, -1); // get the common part no pattern for all the color variants of a particular Tile
      }
      const shouldAddPart = !state.partsList.find(part => part.number.includes(partNoPattern) && part.type === partToAdd.type);
      if (shouldAddPart) {
        const { extraParameters: { total_feet, productId } } = state;
        if (isSMTiltOrFlush(productId)  && total_feet) {
          partToAdd['quantity'] = state.extraParameters.total_feet;
          partToAdd['suggested_quantity'] = state.extraParameters.total_feet;
        }
        state.partsList.push(state.tileReplacementList[group][selectedGroupItem]);
      }
      return { ...state };
    },
    [BomActionTypes.ADD_ATTACHMENT_ITEM](state: bomState, action) {
      const { group, selectedGroupItem } = action.payload;
      const partToAdd = state.attachmentsList[group][selectedGroupItem];
      const partNoPattern = partToAdd.number;
      const shouldAddPart = !state.partsList.find(part => part.number.includes(partNoPattern) && part.type === partToAdd.type);
      if (shouldAddPart) {
        state.partsList.push(state.attachmentsList[group][selectedGroupItem]);
      }
      return { ...state };
    },
    [BomActionTypes.REMOVE_TILE_REPLACEMENT_ITEM](state: bomState, action) {
      const { group, selectedGroupItem } = action.payload;
      const partToRemove = state.tileReplacementList[group][selectedGroupItem];
      let partNoPattern = partToRemove.number;
      if (group === 'tiles') {
        partNoPattern = partNoPattern.slice(0, -1); // get the common part no pattern for all the color variants of a particular Tile
      }
      const partsList = state.partsList.filter(part => !(part.number.includes(partNoPattern) && part.type === partToRemove.type));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_ATTACHMENT_ITEM](state: bomState, action) {
      const { group, selectedGroupItem } = action.payload;
      const partToRemove = state.attachmentsList[group][selectedGroupItem];
      const partNoPattern = partToRemove.number;
      const partsList = state.partsList.filter(part => !(part.number.includes(partNoPattern) && part.type === partToRemove.type));
      return { ...state, partsList };
    },
    [BomActionTypes.ADD_LFOOT](state: bomState) {
      const { partsList, attachmentsList } = state;

      const lFoot = partsList.find((part: bomPart) => part.number.includes('304001')  || part.number.includes('A1LF02M'));

      if (!lFoot) {
        const partToAdd = attachmentsList['2 Piece Aluminum Standoff'].parts[0];
        restorePartQty(state, partToAdd);
        const { extraParameters: { total_feet, productId } } = state;
        if (isSMTiltOrFlush(productId)  && total_feet) {
          partToAdd['quantity'] = state.extraParameters.total_feet;
          partToAdd['suggested_quantity'] = state.extraParameters.total_feet;
        }
        partsList.push(partToAdd);
      }

      return { ...state };
    },
    [BomActionTypes.FETCH_BOM_PART_LIST_REQUEST](state) {
      return { ...state, isLoading: true, isReady: false, error: null, partsList: [] };
    },
    [BomActionTypes.FETCH_BOM_PART_LIST_ERROR](state, action) {
      return { ...state, isLoading: false, error: action.payload.ajaxError };
    },
    [BomActionTypes.FETCH_BOM_PART_LIST_SUCCESS](state, action) {
      console.log('FETCH_BOM_PART_LIST_SUCCESS received with payload:', action.payload);

      // Handle case where payload might be missing or incomplete
      if (!action.payload) {
        console.error('Empty payload in FETCH_BOM_PART_LIST_SUCCESS');
        return {
          ...state,
          isLoading: false,
          isReady: true,
          partsList: [],
          accessoriesList: [],
          attachmentsList: {},
          tileReplacementList: {},
          extraParameters: {},
          total_watts: 0,
          total_modules: 0,
          project_name: '',
          reload_selections: false,
          created: false,
          warningsInfo: null
        };
      }

      const {
        partsList = [],
        accessoriesList = [],
        attachmentsList = {},
        tileReplacementList = {},
        extraParameters = {},
        total_watts = 0,
        total_modules = 0,
        project_name = '',
        reload_selections = false,
        created = false,
        warningsInfo = null
      } = action.payload;

      // Safely map parts with null check
      const restoredPartsList = Array.isArray(partsList) ? partsList.map(part => {
        if (part) {
          restorePartQty(action.payload, part);
        }
        return part;
      }) : [];
      // TODO: need to wok
      // Clear the loader message
      // dispatch({ type: ActionTypes.CLEAR_LOADER_MSG });

      // Preserve the user's accessory selections through a server refresh.
      // The Lite GET returns accessories_list as a catalog with selected:false
      // regardless of user_builds membership, so a naive overwrite wipes any
      // client-driven SELECT_ACCESSORY made before navigation. Merge the prior
      // state's selected flags by part number. develop/master gate the saved
      // selections payload on app.isUbuilderLite (UBuilderLiteContainer and
      // CompleteBomPage). Classic must not take that path: selectAccessory runs
      // only for rows that are not already selected, and that call is what puts
      // the accessory on the parts list. Redux 4 throws if a reducer calls
      // getState() during dispatch, which drops this success action, so the
      // same lite-vs-classic split is read from the route. Classic is
      // /project/bom/...; UBuilder GO routes contain /lite/.
      const priorSelectedNumbers = new Set(
        (state.accessoriesList || [])
          .filter((accessory: any) => accessory && accessory.selected)
          .map((accessory: any) => accessory.number),
      );
      const path = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
      const isLiteFlow = path.indexOf('/lite/') !== -1;
      const selections = extraParameters && extraParameters.selections;
      const savedSelections = isLiteFlow && selections && (selections.accessories || selections.selected_accessories);
      if (Array.isArray(savedSelections)) {
        savedSelections.forEach((num: string) => priorSelectedNumbers.add(num));
      }
      const MLPE_LUG_FAMILY = new Set(['NULGMLP1', 'NULGMLP1-US']);
      const hasMlpeFamilySelected = isLiteFlow && Array.from(priorSelectedNumbers).some((n: string) => MLPE_LUG_FAMILY.has(n));
      const restoredAccessoriesList = Array.isArray(accessoriesList)
        ? accessoriesList.map((accessory: any) => {
          if (!accessory || accessory.selected) return accessory;
          if (priorSelectedNumbers.has(accessory.number)) return { ...accessory, selected: true };
          if (hasMlpeFamilySelected && MLPE_LUG_FAMILY.has(accessory.number)) return { ...accessory, selected: true };
          return accessory;
        })
        : accessoriesList;

      // Explicitly set isLoading to false to ensure the loader disappears
      return {
        ...state,
        isLoading: false,
        isReady: true,
        partsList: restoredPartsList,
        accessoriesList: restoredAccessoriesList,
        attachmentsList,
        tileReplacementList,
        extraParameters,
        total_watts,
        total_modules,
        project_name,
        reload_selections,
        created,
        warningsInfo
      };
    },
    [BomActionTypes.SET_BOM_PRODUCT](state, action) {
      const { productId, projectId } = action.payload;
      return { ...state, productId, projectId };
    },
    [BomActionTypes.BOM_RESET](state: bomState) {
      return {
        total_watts: 0,
        total_modules: 0,
        partsList: [],
        accessoriesList: [],
        attachmentsList: [],
        selectedAttachmentType: [],
        extraParameters: {},
        isLoading: false,
        isReady: false,
        error: null,
        productId: 0,
        projectId: '',
        goToEngineeringFromBom: false,
        stdClampsFetching: false,
        stdClampsFetched: false,
        bomChanged: false,
        created: false,
        isClampsChanged: false,
        liveBomStatus: state.liveBomStatus ?? 'empty',
        liveBomLastUpdatedAt: state.liveBomLastUpdatedAt ?? null,
        liveBomNextRecalculateAt: state.liveBomNextRecalculateAt ?? null,
        liveBomPreviousSnapshot: state.liveBomPreviousSnapshot ?? null,
        liveBomCurrentSnapshot: state.liveBomCurrentSnapshot ?? null,
        liveBomDeltaByPartKey: state.liveBomDeltaByPartKey ?? {},
        liveBomFilters: state.liveBomFilters ?? {
          changedOnly: false,
          warningsOnly: false,
          groupBy: 'none',
          searchText: '',
        },
        liveBomSidebarCollapsed: state.liveBomSidebarCollapsed ?? false,
        liveBomError: state.liveBomError ?? null,
      };
    },
    [BomActionTypes.SET_NEW_PART_QUANTITY](state, action) {
      const partsList = state.partsList;
      const partNumber = action.payload.partNumber;
      const newQty = action.payload.newQty;
      const modifedPartsList = normalizeBomPartsList(partsList.reduce((list, part) => {
        if (part.number === partNumber) {
          part.quantity = newQty;
        }

        list.push(part);
        return list;
      },                                        []));
      return { ...state, partsList: modifedPartsList, ...getLiveBomPartsPatch(state, modifedPartsList) };
    },
    [BomActionTypes.SET_NEW_PART_PRICE](state, action) {
      const { number, price } = action.payload;
      const partsList = [...state.partsList];

      const index = partsList.findIndex(part => part.number === number);
      if (index > -1) {
        partsList[index] = {
          ...partsList[index],
          price: price,
          total_price: 0.00,
        };
      }

      return { ...state, partsList, ...getLiveBomPartsPatch(state, partsList) };
    },


    [BomActionTypes.REMOVE_PART_FROM_BOM](state, action) {
      const { partNumber } = action.payload;
      const partsList = state.partsList.filter(part => part.number !== partNumber);
      return { ...state, partsList, ...getLiveBomPartsPatch(state, partsList) };
    },
    [BomActionTypes.SELECT_TILE_REPLACEMENT_TYPE](state, action) {
      const { group, selectedGroupItem } = action.payload;
      Object.keys(state.tileReplacementList[group]).map(key => {
        state.tileReplacementList[group][key].selected = key === selectedGroupItem;
      });
      return state;
    },
    [BomActionTypes.SELECT_ROOF_ATTACHMENT_TYPE](state, action) {
      const { group, selectedGroupItem } = action.payload;
      const standingSeamMetalGroup = group === "standing seam not verified" || group === "standing seam metal attachments";
      if(state.attachmentsList[group] && (state.attachmentsList[group].hasOwnProperty(selectedGroupItem) || standingSeamMetalGroup)){
        Object.keys(state.attachmentsList[group]).map(key => {
          state.attachmentsList[group][key].selected = key === selectedGroupItem;
        });
      }
      return state;
    },
    [BomActionTypes.SELECT_ATTACHMENT_TYPE](state, action) {
      const { attachmentType } = action.payload;
      Object.keys(state.attachmentsList).map((type) => {
        state.attachmentsList[type].selected = type === attachmentType;
      });
      const allowStandoffAsBasePart = (isSMFamily(state.productId) || isNxtHorizon(state.productId)) && (!state.attachmentsList["2 Piece Aluminum Standoff"].standoffs || !state.attachmentsList["2 Piece Aluminum Standoff"].standoffs.length);
      let partsList = state.partsList.filter(part => (allowStandoffAsBasePart || part.type !== 'Roof Attachment' || part.number.includes('304001') || part.number.includes('304000') || part.number.includes('A1LF02M')) && part.type !== 'Flashing' && part.type !== 'Lag Bolt' && part.type !== 'Washer');

      if (isSFMFamily(state.productId)) {
        const SFMAttachmentPartNumbers = getSFMAttachmentsPartsNumbers(state.attachmentsList);
        partsList = state.partsList.filter(part => !SFMAttachmentPartNumbers.includes(part.number));
      }

      const selectedAttachmentType = state.attachmentsList[attachmentType];

      if (state.productId !== 12) {
        partsList = addSelectedFlashingToPartsList(selectedAttachmentType, partsList, state.extraParameters);
        partsList = addSelectedStandoffsToPartsList(selectedAttachmentType, partsList, state.extraParameters);
      }

      if (isSFMFamily(state.productId)) {
        partsList = addSFMAttachmentsToPartsList(selectedAttachmentType.parts, partsList);
      }

      partsList = enableAllParts(partsList);

      return { ...state, selectedAttachmentType, partsList };
    },
    [BomActionTypes.SELECT_ATTACHMENT_TYPE_ON_LOAD](state, action) {
      const { attachmentType } = action.payload;
      const selectedAttachmentType = state.attachmentsList[attachmentType];
      return { ...state, selectedAttachmentType };
    },
    [BomActionTypes.SELECT_ATTACHMENT](state, action) {
      const { attachmentGroup, attachmentNumber } = action.payload;
      state.selectedAttachmentType[attachmentGroup] = state.selectedAttachmentType[attachmentGroup].map(attachment => selectAttachment(attachment, attachmentNumber));

      return { ...state };
    },
[BomActionTypes.SELECT_FLASHING_ATTACHMENT](state, action) {
   const { attachmentGroup, attachmentNumber } = action.payload;
   const attachments = state.selectedAttachmentType[attachmentGroup];
   if (!attachments) return state;
   let updatedAttachments;

   if (attachmentNumber === '') {
       updatedAttachments = attachments.map(a => ({
           ...a,
           selected: a.number === ''
       }));
   }

   else {
       updatedAttachments = attachments.map(a => {
           if (a.number === '') {
               return { ...a, selected: false };
           }
           if (a.number === attachmentNumber) {
               return { ...a, selected: !a.selected };
           }
           return {...a};
       });

       const anySelected = updatedAttachments.some(a => a.number !== '' && a.selected);
       if (!anySelected) {
           updatedAttachments = updatedAttachments.map(a => ({
               ...a,
               selected: a.number === ''
           }));
       }
   }

  return {
    ...state,
    selectedAttachmentType: {
        ...state.selectedAttachmentType,
        [attachmentGroup]: updatedAttachments
    },
    extraParameters: {
        ...state.extraParameters,
        selections: {
            ...state.extraParameters?.selections,
            flashings: (() => {
                const selected = updatedAttachments
                    .filter(a => a.number !== '' && a.selected)
                    .map(a => a.number);

                return selected;
            })()
        }
    }
};
},
    [BomActionTypes.SELECT_LAG_BOLTS](state) {
      if (state.selectedAttachmentType.lag_bolts) {
        state.selectedAttachmentType.lag_bolts[0].selected = true;
      }
      return { ...state };
    },
    [BomActionTypes.UNSELECT_LAG_BOLTS](state) {
      if (state.selectedAttachmentType.lag_bolts && state.selectedAttachmentType.lag_bolts.length) {
        state.selectedAttachmentType.lag_bolts[0].selected = false;
      }
      return { ...state };
    },
    [BomActionTypes.DISABLE_LAG_BOLTS](state) {
      if (state.selectedAttachmentType.lag_bolts && state.selectedAttachmentType.lag_bolts.length) {
        state.selectedAttachmentType.lag_bolts[0].disabled = true;
      }
      return { ...state };
    },
    [BomActionTypes.ENABLE_LAG_BOLTS](state) {
      if (state.selectedAttachmentType.lag_bolts && state.selectedAttachmentType.lag_bolts.length) {
        state.selectedAttachmentType.lag_bolts[0].disabled = false;
      }
      return { ...state };
    },
    [BomActionTypes.DISABLE_PART](state, action) {
      const { partNumber } = action.payload;
      const partsList = state.partsList.map((part) => {
        if (part.number === partNumber) {
          part.disabled = true;
        }

        return part;
      });

      return { ...state, partsList };
    },
    [BomActionTypes.ENABLE_PART](state, action) {
      const { partNumber } = action.payload;
      const partsList = state.partsList.map((part) => {
        if (part.number === partNumber) {
          part.disabled = false;
        }

        return part;
      });

      return { ...state, partsList };
    },
    [BomActionTypes.FETCH_STANDARD_CLAMPS](state) {
      return { ...state, stdClampsFetching: true };
    },


    [BomActionTypes.REMOVE_FRONT_TRIM_END_CAP](state) {
      const partsList = state.partsList.filter(part => part.type !== 'Trim Clip');

      return { ...state, partsList };
    },
    [BomActionTypes.CHANGE_BOM_COLOR_VARIANT](state, action) {
      const { partNrToReplace, newPart } = action.payload;
      const newPartsList = state.partsList.map((part) => {
        if (part.number === partNrToReplace) {
          return newPart;
        }
        return part;
      });
      return { ...state, partsList: newPartsList };
    },
    [BomActionTypes.SET_BOM_PK](state, action) {
      const { bomPk } = action.payload;
      return { ...state, bomPk };
    },
    [BomActionTypes.CLEAR_BOM_PK](state, action) {
      return { ...state, bomPk: null };
    },
    [BomActionTypes.REMOVE_TILE_HOOK](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('004008'));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_LFOOT](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('304001') && !part.number.includes('304000') && !part.number.includes('A1LF02M'));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_FLASHKIT_PRO](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('004050'));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_FLASHKIT_PRO_SB](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('004015'));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_FLASHLOC_COMP_KIT](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('004085'));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_STRONGHOLD_ATT_COMP_NON_SEALANT](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('SHCPKT'));
      return { ...state, partsList };
    },
    [BomActionTypes.REMOVE_STRONGHOLD_ATT_KIT_COMP](state: bomState) {
      const partsList = state.partsList.filter(part => !part.number.includes('SHCPKT'));
      return { ...state, partsList };
    },
    [BomActionTypes.SELECT_ACCESSORY](state: bomState, action) {
      const { partNumber } = action.payload;
      const { accessoriesList } = state;

      const index = accessoriesList.findIndex(accessory => accessory.number === partNumber);

      if (index > -1) {
        state.accessoriesList[index].selected = true;
      }

      return { ...state };
    },
    [BomActionTypes.UNSELECT_ACCESSORY](state: bomState, action) {
      const { partNumber } = action.payload;
      const { accessoriesList } = state;

      const index = accessoriesList.findIndex(accessory => accessory.number === partNumber);

      if (index > -1) {
        state.accessoriesList[index].selected = false;
      }

      return { ...state };
    },
    [BomActionTypes.BOM_SELECTION_LOADING_START](state) {
      return { ...state, bomSelectionsLoading: true };
    },
    [BomActionTypes.BOM_SELECTION_LOADING_STOP](state: bomState) {
      return { ...state, bomSelectionsLoading: false };
    },
    [BomActionTypes.GO_TO_ENGINEERING_FROM_BOM](state) {
      return { ...state, goToEngineeringFromBom: true };
    },
    [BomActionTypes.DONT_GO_TO_ENGINEERING_FROM_BOM](state) {
      return { ...state, goToEngineeringFromBom: false };
    },
    [BomActionTypes.REMOVE_PART_FROM_REMEMBERED_QTYS](state: bomState, action: { payload: { ids: string[] } }) {
      const { ids } = action.payload;
      ids.map(id => delete state.extraParameters.selections[id]);
      return { ...state };
    },
    [BomActionTypes.BOM_CHANGED](state: bomState) {
      return { ...state, bomChanged: true, created: false };
    },
    [BomActionTypes.SELECT_BALLAST_RAIL_LENGTH](state: bomState, action) {
      const { length } = action.payload;
      const ballastRailSelection = state.extraParameters.ballast_rail_selection;
      const selectedOption = ballastRailSelection.options.find(
        option => option.length === length && !option.disabled
      );
      if (!selectedOption || length === ballastRailSelection.selected_length) {
        return state;
      }

      return {
        ...state,
        extraParameters: {
          ...state.extraParameters,
          ballast_rail_selection: {
            ...ballastRailSelection,
            selected_length: length,
            manual_selection: true,
          },
          selections: {
            ...state.extraParameters.selections,
            ballast_rail_length: length,
          },
        },
      };
    },
    [BomActionTypes.CLAMPS_CHANGED](state: bomState, action) {
      const clampsChanged = action.payload.clampsChanged ? true : false;
      return { ...state, isClampsChanged: clampsChanged };
    },
    [BomActionTypes.UPDATE_QUANTITY](state: bomState, action) {
      const { accessory, projectEnvConfig, projectVersion } = action.payload;
      const productId = state.productId;
      const extraParameters = state.extraParameters;
      if(accessory.number === nxtMlpeMountDomesticPart(projectVersion, projectEnvConfig?.is_domestic_mlpe_required) || accessory.number === 'LUGMLPE1M'|| accessory.number === 'LUGMLPE1R') {
        const bom_part = state.partsList.find(part => part.number === nxtMlpeMountDomesticPart(projectVersion, projectEnvConfig?.is_domestic_mlpe_required));
        const nxt_horizon_for_lug_selected =  state.accessoriesList.find(acc => acc.number === nxtMlpeMountDomesticPart(projectVersion, projectEnvConfig?.is_domestic_mlpe_required)).selected;
        const nxt_horizon_for_mlpe_selected = state.accessoriesList.find(acc => acc.number === 'LUGMLPE1M').selected;
        const nxt_horizon_for_row_to_row_bonding_selected = extraParameters.row_to_row_bonding_qty > 0 ? state.accessoriesList.find(acc => acc.number === 'LUGMLPE1R').selected : false;
        if(bom_part) {
          if(nxt_horizon_for_mlpe_selected && nxt_horizon_for_lug_selected && nxt_horizon_for_row_to_row_bonding_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = [extraParameters.total_array_qty + state.total_modules + (extraParameters.row_to_row_bonding_qty) , extraParameters.total_array_qty + state.total_modules + (extraParameters.row_to_row_bonding_qty)];
          }
          else if (nxt_horizon_for_row_to_row_bonding_selected && nxt_horizon_for_mlpe_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = [(extraParameters.row_to_row_bonding_qty) + state.total_modules, (extraParameters.row_to_row_bonding_qty) + state.total_modules];
          }
          else if (nxt_horizon_for_row_to_row_bonding_selected && nxt_horizon_for_lug_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = [extraParameters.total_array_qty + (extraParameters.row_to_row_bonding_qty) , extraParameters.total_array_qty + (extraParameters.row_to_row_bonding_qty)];
          }
          else if(nxt_horizon_for_mlpe_selected && nxt_horizon_for_lug_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = isNxtHorizon(productId) ? [state.total_modules , state.total_modules] : [extraParameters.total_array_qty + state.total_modules , extraParameters.total_array_qty + state.total_modules];
          }
          else if (!projectEnvConfig?.is_domestic_mlpe_required && nxt_horizon_for_mlpe_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = [state.total_modules, state.total_modules];
          }
          else if (nxt_horizon_for_lug_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = [extraParameters.total_array_qty, extraParameters.total_array_qty];
          }
          else if (nxt_horizon_for_row_to_row_bonding_selected) {
            [bom_part.quantity,bom_part.suggested_quantity] = [extraParameters.row_to_row_bonding_qty, extraParameters.row_to_row_bonding_qty];
          }
        }
      }
      else {
        const bom_part = state.partsList.find(part => part.number === accessory.number);
        const rm_roof_pad = state.extraParameters.rm_roof_pad;
        if (!rm_roof_pad.is_selected ){
          [rm_roof_pad.qty, bom_part.quantity, bom_part.suggested_quantity] = [bom_part.quantity, accessory.quantity, accessory.suggested_quantity];
        }
        else{
          [accessory.quantity, bom_part.quantity, bom_part.suggested_quantity] = [bom_part.quantity, rm_roof_pad.qty, rm_roof_pad.suggestedQty];}
        rm_roof_pad.is_selected = !rm_roof_pad.is_selected;
      }
      const partsList = normalizeBomPartsList(state.partsList);
      return { ...state, partsList, ...getLiveBomPartsPatch(state, partsList) };
    },
    [BomActionTypes.ADD_RM10_EVO_MODULE_CLAMP_SIDE_BOLT_BULK](state, action) {
      const { qty } = action.payload;
      let { parts } = action.payload;
      parts = parts.map((part) => {
        part.quantity = qty;
        part.suggested_quantity = qty;
        restorePartQty(state, part);
        return part;
      });
      const partsList = state.partsList
        .filter(part => part.number !== PART_RM10_EVO_MODULE_CLAMP_KIT)
        .concat(parts);
      return { ...state, partsList };
    },
    [BomActionTypes.ADD_RM10_EVO_MODULE_CLAMP_BULK](state, action) {
      const { qty } = action.payload;
      let { parts } = action.payload;
      parts = parts.map((part) => {
        part.quantity = qty;
        part.suggested_quantity = qty;
        restorePartQty(state, part);
        return part;
      });
      const partsList = state.partsList
        .filter(part => part.number !== PART_RM10_EVO_MODULE_CLAMP_KIT)
        .concat(parts);
      return { ...state, partsList };
    },
    [BomActionTypes.ADD_T_BOLTS_009020S_FOR_SOLARHOOKS](state, action) {
      const { qty } = action.payload;
      let { parts } = action.payload;
      parts = parts.map((part) => {
        part.quantity = qty;
        part.suggested_quantity = qty;
        restorePartQty(state, part);
        return part;
      });
      const partsList = state.partsList
        .filter(part => part.number !== BND_T_BOLT_NUT_3_8_3_4_SS)
        .concat(parts);
      return { ...state, partsList };
    },
    [BomActionTypes.SET_AURORA_REPORTS_DATA_URLS](state: bomState, action) {
      const auroraReportsDataUrl = action.payload.auroraReportsDataUrl;

      state.auroraReport.auroraReportsDataUrl = auroraReportsDataUrl;
      state.auroraReport.show = true;

      return { ...state };
    },
    [BomActionTypes.SET_OS_REPORTS_DATA_URLS](state: bomState, action) {
      const osReportsDataUrl = action.payload.osReportsDataUrl

      state.osReport.osReportsDataUrl = osReportsDataUrl;
      state.osReport.show = true;

      return { ...state };
    },
    [BomActionTypes.SET_OS_REPORTS_DATA_URLS](state: bomState, action) {
      const osReportsDataUrl = action.payload.osReportsDataUrl

      state.osReport.osReportsDataUrl = osReportsDataUrl;
      state.osReport.show = true;

      return { ...state};
    },
    [BomActionTypes.SEND_BOM_DATA_TO_AURORA_SUCCESS](state: bomState) {
      state.auroraReport.loading = false;
      return { ...state };
    },
    [BomActionTypes.SAVE_BOM_FOR_AURORA](state: bomState) {
      return { ...state, auroraReport: { show: true, loading: true } };
    },
    [BomActionTypes.SEND_BOM_DATA_TO_OS_SUCCESS](state: bomState) {
      state.osReport.loading = false;
      return { ...state };
    },
    [BomActionTypes.SAVE_BOM_FOR_OS](state: bomState) {
      return { ...state, osReport: {show: true, loading: true}};
    },
    [BomActionTypes.HIDE_MODAL_FOR_AURORA_REPORTS](state: bomState) {
      state.auroraReport.show = false;
      state.auroraReport.loading = false;
      return { ...state};
    },
    [BomActionTypes.HIDE_MODAL_FOR_OS_REPORTS](state: bomState) {
      state.osReport.show = false;
      state.osReport.loading = false;
      return { ...state};
    },
    [BomActionTypes.ENABLE_RETRY_BUTTON_FOR_SEND_BOM_DATA](state: bomState) {
      state.auroraReport.diableRetryButton = false;
      return { ...state };
    },
    [BomActionTypes.DISABLE_RETRY_BUTTON_FOR_SEND_BOM_DATA](state: bomState) {
      state.auroraReport.diableRetryButton = true;
      return { ...state };
    },
    [BomActionTypes.ENABLE_RETRY_BUTTON_FOR_SEND_BOM_DATA_OS](state: bomState) {
      state.osReport.diableRetryButton = false;
      return { ...state};
    },
    [BomActionTypes.DISABLE_RETRY_BUTTON_FOR_SEND_BOM_DATA_OS](state: bomState) {
      state.osReport.diableRetryButton = true;
      return { ...state };
    },
    [BomActionTypes.SET_AURORA_LOADER](state: bomState) {
      state.auroraReport.loading = true;
      return { ...state };
    },
    [BomActionTypes.CLEAR_AURORA_LOADER](state: bomState) {
      state.auroraReport.loading = false;
      return { ...state };
    },
    [BomActionTypes.SET_AURORA_ERROR](state: bomState, action) {
      state.auroraReport.errorMessages = action.payload.errorMessages;
      state.auroraReport.error = true;
      return { ...state };
    },
    [BomActionTypes.CLEAR_AURORA_ERROR](state: bomState) {
      state.auroraReport.errorMessages = null;
      state.auroraReport.error = false;
      return { ...state };
    },
    [BomActionTypes.SET_OS_LOADER](state: bomState) {
      state.osReport.loading = true;
      return { ...state };
    },
    [BomActionTypes.CLEAR_OS_LOADER](state: bomState) {
      state.osReport.loading = false;
      return { ...state };
    },
    [BomActionTypes.SET_OS_ERROR](state: bomState, action) {
      state.osReport.errorMessages = action.payload.errorMessages;
      state.osReport.error = true;
      return { ...state };
    },
    [BomActionTypes.CLEAR_OS_ERROR](state: bomState) {
      state.osReport.errorMessages = null;
      state.osReport.error = false;
      return { ...state };
    },

    [SaveLoadProjectActionTypes.SAVE_PROJECT](state: bomState) {
      if (state.liveBomStatus === 'empty') return state;
      return { ...state, liveBomStatus: 'stale', liveBomNextRecalculateAt: null };
    },

    [SaveLoadProjectActionTypes.AUTO_SAVE_PROJECT](state: bomState) {
      if (state.liveBomStatus === 'empty') return state;
      return { ...state, liveBomStatus: 'stale' };
    },

    [BomActionTypes.LIVE_BOM_MARK_STALE](state: bomState) {
      if (state.liveBomStatus === 'empty') return state;
      return { ...state, liveBomStatus: 'stale' };
    },

    [BomActionTypes.LIVE_BOM_SCHEDULE_REFETCH](state: bomState, action) {
      if (state.liveBomStatus === 'empty' || state.liveBomStatus === 'recalculating') return state;
      return {
        ...state,
        liveBomStatus: 'stale',
        liveBomNextRecalculateAt: action.payload?.nextRecalculateAt || null,
      };
    },

    [SaveLoadProjectActionTypes.AUTO_SAVE_PROJECT_FINISHED](state: bomState) {
      if (state.liveBomStatus === 'empty' || state.liveBomStatus === 'recalculating') return state;

      const currentScheduledAt = state.liveBomNextRecalculateAt
        ? new Date(state.liveBomNextRecalculateAt).getTime()
        : 0;
      const fallbackScheduledAt = Date.now() + LIVE_BOM_AUTO_REFETCH_DEBOUNCE_MS;

      return {
        ...state,
        liveBomStatus: 'stale',
        liveBomNextRecalculateAt: new Date(
          currentScheduledAt > Date.now() ? currentScheduledAt : fallbackScheduledAt,
        ).toISOString(),
      };
    },

    [BomActionTypes.LIVE_BOM_FETCH_REQUEST](state: bomState, action) {
      const { projectId, productId } = action?.payload || {};
      const numProductId = products[productId] || state.productId;
      return {
        ...state,
        liveBomStatus: 'recalculating',
        liveBomNextRecalculateAt: null,
        liveBomError: null,
        projectId: projectId || state.projectId,
        productId: numProductId,
      };
    },

    [BomActionTypes.LIVE_BOM_FETCH_SUCCESS](state: bomState, action) {
      const {
        partsList,
        warningsInfo,
        total_modules,
        total_watts,
        accessoriesList,
        roofAttachments,
        tileReplacementList,
        extraParameters,
        project_name,
      } = action.payload;
      const normalizedPartsList = normalizeBomPartsList(partsList || []);
      const previous = state.liveBomCurrentSnapshot
        ? normalizeBomPartsList(state.liveBomCurrentSnapshot)
        : null;
      const deltaByPartKey: Record<string, number> = {};

      if (previous) {
        const prevMap: Record<string, number> = {};
        previous.forEach((p: bomPart) => { prevMap[p.number] = p.quantity; });
        normalizedPartsList.forEach((p: bomPart) => {
          const prevQty = prevMap[p.number] !== undefined ? prevMap[p.number] : 0;
          const delta = p.quantity - prevQty;
          if (delta !== 0) deltaByPartKey[p.number] = delta;
        });
        Object.keys(prevMap).forEach((num) => {
          if (!normalizedPartsList.find((p: bomPart) => p.number === num)) {
            deltaByPartKey[num] = -prevMap[num];
          }
        });
      }

      const productIdStr = extraParameters?.productId != null
        ? String(extraParameters.productId)
        : state.productId != null ? String(state.productId) : '';

      const parsedAttachments = roofAttachments
        ? parseAttachmentsList(productIdStr, roofAttachments)
        : state.attachmentsList;

      const hasWarnings = warningsInfo?.warnings?.length > 0;
      return {
        ...state,
        liveBomStatus: hasWarnings ? 'warning' : 'synced',
        liveBomLastUpdatedAt: new Date().toISOString(),
        liveBomNextRecalculateAt: null,
        liveBomPreviousSnapshot: previous,
        liveBomCurrentSnapshot: normalizedPartsList,
        liveBomDeltaByPartKey: deltaByPartKey,
        liveBomError: null,
        partsList: normalizedPartsList,
        accessoriesList: accessoriesList || state.accessoriesList,
        attachmentsList: parsedAttachments || state.attachmentsList,
        tileReplacementList: tileReplacementList || state.tileReplacementList,
        extraParameters: extraParameters && Object.keys(extraParameters).length > 0
          ? extraParameters
          : state.extraParameters,
        total_modules: total_modules ?? state.total_modules,
        total_watts: total_watts ?? state.total_watts,
        project_name: project_name || state.project_name,
        warningsInfo: warningsInfo || state.warningsInfo,
        isReady: true,
      };
    },

    [BomActionTypes.LIVE_BOM_FETCH_ERROR](state: bomState, action) {
      return {
        ...state,
        liveBomStatus: 'error',
        liveBomNextRecalculateAt: null,
        liveBomError: action.payload?.error || 'BOM update failed.',
      };
    },

    [BomActionTypes.LIVE_BOM_SET_SIDEBAR_COLLAPSED](state: bomState, action) {
      return { ...state, liveBomSidebarCollapsed: action.payload.collapsed };
    },

    [BomActionTypes.LIVE_BOM_SET_FILTERS](state: bomState, action) {
      return {
        ...state,
        liveBomFilters: { ...state.liveBomFilters, ...action.payload.filters },
      };
    },

    [BomActionTypes.LIVE_BOM_CLEAR_DELTAS](state: bomState) {
      return { ...state, liveBomDeltaByPartKey: {} };
    },
  }),
};
